import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { Timesheet } from "domain/entities/timesheet";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { TimesheetDayInputFilter } from "domain/models/timesheet-day-input-filter";
import { translateText } from "infra/translation/translate";
import { useAbscenceTypesByFilter } from "presentation/hooks/use-query-abscence-type";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDocumentByFilter,
} from "presentation/hooks/use-query-document";
import { useTimesheetsById } from "presentation/hooks/use-query-timesheet";
import { useTimesheetDaysByFilter } from "presentation/hooks/use-query-timesheet-day";
import { DocumentUpload } from "presentation/stores/use-document-upload-store";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./index.css";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EMAIL } from "domain/enum/email";

function TimesheetDocuments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const { userEmployee, email: userEmployeeEmail } = useAuthStore();

  // Queries
  const { data: abscencesData } = useAbscenceTypesByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  } as AbscenceTypeInputFilter);

  const { data: timesheetData } = useTimesheetsById(id);

  const { data: timesheetDayData } = useTimesheetDaysByFilter({
    limit: 100,
    sortBy: "day",
    sortOrder: "asc",
    timesheet: id,
  } as TimesheetDayInputFilter);

  const {
    data: documentsData,
    refetch: refetchDocuments,
    isFetched,
  } = useDocumentByFilter({
    limit: 100,
    timesheetId: id,
  } as DocumentInputFilter);

  const {
    mutate: createDocumentMutation,
    isError: isCreateDocError,
    isSuccess: isCreateDocSuccess,
  } = useCreateDocumentMutation();

  const { mutate: deleteDocumentMutation, isSuccess: isDeleteDocSuccess } =
    useDeleteDocumentMutation();

  // Store
  const [documentsUpload, setDocumentsUpload] = useState<DocumentUpload[]>();
  const [currentTimesheet, setCurrentTimesheet] = useState<Timesheet>();

  // Identifica dias e ocorrencias que precisam de justificação
  useEffect(() => {
    getDocuments(documentsData?.data || []);
  }, [abscencesData, timesheetDayData, timesheetData, documentsData]);

  useEffect(() => {
    const fn = async () => {
      const docs = await refetchDocuments();
      if (docs?.data?.data) getDocuments(docs.data.data || []);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Documentos guardado com sucesso",
        life: 1500,
      });
    };

    if (isCreateDocSuccess) fn();
  }, [isCreateDocSuccess]);

  useEffect(() => {
    const fn = async () => {
      const docs = await refetchDocuments();
      if (docs?.data?.data) getDocuments(docs.data.data || []);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Documentos removido com sucesso",
        life: 1500,
      });
    };

    if (isDeleteDocSuccess) fn();
  }, [isDeleteDocSuccess]);

  useEffect(() => {
    if (isCreateDocError) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Falha ao guardar documentos",
        life: 1500,
      });
    }
  }, [isCreateDocError]);

  const getDocuments = (documents: Document[]) => {
    let currentDocuments: DocumentUpload[] = [];
    timesheetDayData?.data?.map((item) => {
      if (item.abscenceType && item.abscenceType.id) {
        const abscence = abscencesData?.data?.find(
          (subitem) => subitem.id === item.abscenceType?.id
        );
        if (abscence && abscence.documentRequired) {
          const document = documents.find(
            (subitem) => subitem.timesheetDay.id === item.id
          );

          const title = abscence?.name || "Comprovativo de Ausência";
          const timesheet =
            timesheetData && timesheetData.data
              ? timesheetData.data
              : undefined;
          setCurrentTimesheet(timesheet);
          const eventDate = new Date(
            timesheet?.year ?? new Date().getFullYear(),
            timesheet?.month ? timesheet?.month - 1 : new Date().getMonth(),
            item.day
          );
          currentDocuments.push({
            id: document?.id ?? "",
            name: `${eventDate.toLocaleDateString("pt-PT", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}: ${title}`,
            docTypeId: abscence?.documentType?.id,
            url: document?.fullPath ?? undefined,
            referenceId: item.id,
          });
        }
      }
    });
    setDocumentsUpload(currentDocuments);
  };

  const removeDocument = async (e: any, doc?: DocumentUpload) => {
    if (!doc?.id || !doc.url) return;

    const currentyDay =
      String(currentTimesheet?.month).padStart(2, "0") +
      "-" +
      currentTimesheet?.year;

    try {
      await httpClient.delete(
        `/assets/timesheets/${currentTimesheet?.employee.id}/${currentyDay}/Ausencias`,
        {
          data: { filePath: doc.url },
        }
      );

      const currentDocument: Document = {
        id: doc.id,
        documentType: { id: doc.docTypeId },
        employee: { id: currentTimesheet?.employee?.id || "" },
        fullPath: doc.url,
        timesheetDay: { id: doc.referenceId },
        lastModifiedBy: userEmployeeEmail ?? EMAIL,
      };

      deleteDocumentMutation(currentDocument);
    } catch (err) {
      console.error("Delete file failed:", err);
    }
  };

  const handleUpload = async (
    event: any,
    index: number,
    docTypeId: string | undefined,
    docName: string
  ) => {
    const file = event.files[0];
    if (file && currentTimesheet?.employee?.id) {
      const formData = new FormData();
      const file = event.files[0];
      const extension = file.name.split(".").pop();
      const safeDocName = docName
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\//g, "-")
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/gi, "")
        .replace(/-+/g, "-")
        .toLowerCase();
      const fileNameWithExtension = `${safeDocName}.${extension}`;
      formData.append("file", file, fileNameWithExtension);
      const currentyDay =
        String(currentTimesheet?.month).padStart(2, "0") +
        "-" +
        currentTimesheet?.year;

      try {
        const response = await httpClient.post(
          `/assets/timesheets/${currentTimesheet.employee.id}/${currentyDay}/Ausencias`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const filePath = response.data.filePath;

        const fileSelected = documentsUpload?.find(
          (item) => item.name === docName
        );
        if (filePath && fileSelected) {
          fileSelected.url = filePath;

          const currentDocument: Document = {
            id: fileSelected?.id,
            documentType: { id: docTypeId },
            employee: { id: currentTimesheet.employee.id },
            fullPath: filePath,
            timesheetDay: { id: fileSelected?.referenceId },
            lastModifiedBy: userEmployeeEmail ?? EMAIL,
          };

          if (currentDocument.id) deleteDocumentMutation(currentDocument);
          createDocumentMutation(currentDocument);
        }
      } catch (error) {
        console.error("Erro no upload:", error);
      }
    }
  };

  const openDocument = async (doc: DocumentUpload) => {
    if (!doc.url) return;

    try {
      const response = await httpClient.get(
        `/assets/${doc.url}`
      );
      const signedUrl = response.data.url;
      if (signedUrl) window.open(signedUrl, "_blank");
    } catch (err) {
      console.error("Erro ao abrir documento:", err);
    }
  };

  return (
    <>
      <p className="font-bold">Comprovativos de Justificação de Ausência:</p>
      <div className="grid">
        {documentsUpload?.map((doc: DocumentUpload, index: number) => (
          <div key={index} className="col-12 md:col-6 lg:col-4 p-2">
            <div className="flex flex-column align-items-center border-1 document-border-color p-3 ">
              <p className="font-bold">{translateText(doc.name, "pt")}</p>

              {doc.url ? (
                <div className="spacing-between-buttons">
                  <Button
                    label={translateText("View", "pt")}
                    icon="pi pi-eye"
                    className="p-button-sm p-button-outlined"
                    onClick={() => openDocument(doc)}
                  />
                  <Button
                    label={translateText("Remove", "pt")}
                    icon="pi pi-trash"
                    className="p-button-sm p-button-outlined"
                    severity="danger"
                    onClick={(e) => removeDocument(e, doc)}
                  />
                </div>
              ) : (
                <FileUpload
                  mode="basic"
                  accept=".pdf,.jpg,.png"
                  maxFileSize={10000000}
                  customUpload
                  auto
                  uploadHandler={(e) =>
                    handleUpload(e, index, doc.docTypeId, doc.name)
                  }
                  chooseLabel={translateText("Upload", "pt")}
                  className="p-mt-2"
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="default-4-cols button-align">
        <Button
          label="Voltar"
          className="button-toolbar "
          onClick={() => navigate(-1)}
        />
      </div>
      <Toast ref={toast} position="bottom-right" />
    </>
  );
}

export default TimesheetDocuments;
